import { useEffect, useState, type RefObject } from 'react';

/**
 * The width below which the home's two-column rows stack.
 *
 * **This number lives in three places and they must agree**: the `@container` queries in
 * `DesktopHomeHeader.module.css` and `CatchUpSections.module.css`, and here. CSS cannot read a TS
 * constant, so `test/home-reflows-with-the-pane.test.ts` asserts the CSS matches this value.
 *
 * At 700 the ⅓ column is ~223px — about the narrowest the QR block and the Files card read at.
 */
export const HOME_STACK_BREAKPOINT_PX = 700;

/**
 * The width at which the whole Files browser folds to its phone form.
 *
 * **This number lives in TWO places and they must agree**: every `@container files (max-width: 600px)`
 * query under `components/symphony/files/` — the control band, the table's row cards, the folder rows,
 * the thumbnail grid — and here, where `FilesBrowser` / `FilesFlatList` measure the very box those
 * queries are asked about and hand the answer down as `narrowPane`.
 *
 * ⚠️ **CHANGE ONE AND YOU MUST CHANGE THE OTHER.** CSS cannot read a TS constant and a `@container`
 * query cannot read a custom property (`@container (max-width: var(--x))` is not a thing), so the two
 * cannot be collapsed into one literal. `test/files-redesign.test.ts` § "§17" asserts that every
 * `files` container query in the browser's stylesheets states exactly this number — that assertion is
 * the only thing keeping them equal, and they have desynced before (the band once folded on a
 * `@media (max-width: 720px)` while the rows folded on a `@container (max-width: 460px)`).
 *
 * Why this and not `NARROW_BREAKPOINT_PX` (the viewport's 720): the Files pane is already narrowed by
 * the sidebar rail, the docked chat and the side panel, so the viewport was never the width that
 * decided this layout. A viewport of 1440 with a docked chat leaves the pane around 590 — a phone
 * layout on a desktop window, which is precisely the case that used to hide the row's share glyph
 * (CSS, container) while withholding the sheet's Share item (JS, viewport) and leave no way to grant
 * access at all.
 */
export const FILES_FOLD_BREAKPOINT_PX = 600;

/**
 * `useNarrow`, but measuring an ELEMENT instead of the window.
 *
 * The home needs this because the thing that narrows it is the docked chat, which takes 370px out of
 * the content pane through `--chat-w` and leaves `window.innerWidth` untouched — so a `matchMedia`
 * query cannot see it (Monday 12734251677). Anything whose rendering is coupled to a row that stacks
 * on a `@container` query has to observe the same box the query does, or the two disagree: the row
 * restacks to one column while its contents keep rendering their full-width desktop form.
 *
 * **`threshold` is read the way CSS reads `max-width`: INCLUSIVE.** `@container (max-width: 600px)`
 * matches at exactly 600, so this returns `true` at exactly 600 too. It used to be a strict `<`, which
 * put the JS twin and the query it mirrors on opposite sides of a single pixel.
 *
 * Returns `false` until the first measurement, matching `useNarrow`'s pre-effect default, so SSR and
 * the first client paint agree on the desktop rendering. For a consumer that gates an ACTION rather
 * than a layout, that default is what keeps the transient coherent rather than empty: the pre-measure
 * render is the whole WIDE row (its glyph present, its menu item absent), never a row with neither.
 */
export function useContainerNarrow(ref: RefObject<HTMLElement | null>, threshold: number): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Measure ONCE, synchronously, before the observer is wired: `ResizeObserver` delivers its first
    // callback asynchronously, so without this the element renders its pre-measure default for a frame
    // even though its width was knowable the moment it mounted. It is also the only measurement that
    // happens at all where there is no `ResizeObserver` (jsdom, old Safari) — same fallback
    // `useMeasuredWidth` makes.
    setNarrow(el.getBoundingClientRect().width <= threshold);
    if (typeof ResizeObserver === 'undefined') return undefined;
    // ResizeObserver rather than a resize listener: the pane's width changes when the chat docks or
    // the rail collapses, neither of which resizes the window.
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width;
      // `<=`, because the `@container (max-width: N)` query this mirrors matches AT N.
      if (width !== undefined) setNarrow(width <= threshold);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return narrow;
}
